import { useState } from 'react';
import { ApiError } from '../../core/api/api-error';
import {
    getPortalSigningDocumentContent,
    getPortalSigningPackage,
    submitPortalEcpSignature,
    submitPortalSignature
} from '../../core/api/portal-service';
import { formatPlainDate } from '../../core/dates/plain-date';
import { describeApiError } from '../../core/errors/describe-api-error';
import { describeSignatureError } from '../../core/errors/describe-signature-error';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { PortalSignatureRequest } from '../../core/models/portal-signature-request';
import type { PortalSigningDocument } from '../../core/models/portal-signing-document';
import { signPackageHash } from '../../core/signing/ncalayer-client';
import { Alert } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { EmptyState } from '../../ui/empty-state';
import { DocumentIcon, DownloadIcon, SignatureIcon } from '../../ui/portal-icons';
import { SignaturePad } from '../../ui/signature-pad';

/**
 * What the customer has been asked to sign on this case, and the pad they sign it on.
 *
 * **The documents come first and are always readable.** A signature over a title is not agreement
 * to a contract, so each frozen document downloads from the request itself, whether or not anybody
 * shared it — and the pad opens beneath them rather than in place of them.
 *
 * **Two ways to sign, and the office's requirement decides which are offered.** A drawing is made on
 * the pad in the customer's own session; an ЭЦП is made by NCALayer on their computer over the hash
 * the server issues. Either way the request is awaiting a signature or it is signed — there is no
 * "being checked" state, because the server checks before it answers.
 */
export function SignatureRequestList({
    requests,
    onSigned
}: {
    requests: PortalSignatureRequest[];
    onSigned: () => void;
}) {
    const { t } = useTranslation();
    const language = useLanguage();
    const [openId, setOpenId] = useState<string | null>(null);
    const [strokes, setStrokes] = useState<number[][][] | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<TranslationKey | null>(null);
    const [ecpBusyId, setEcpBusyId] = useState<string | null>(null);
    const [ecpError, setEcpError] = useState<{ requestId: string; key: TranslationKey } | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [unavailableId, setUnavailableId] = useState<string | null>(null);

    if (requests.length === 0) {
        return (
            <EmptyState icon={<SignatureIcon />} title={t('portal.no_signature_requests')}>
                {t('portal.no_signature_requests_hint')}
            </EmptyState>
        );
    }

    function open(request: PortalSignatureRequest) {
        setOpenId(request.id);
        setStrokes(null);
        setConfirmed(false);
        setError(null);
        setEcpError(null);
    }

    /**
     * The hash is asked for at the moment of signing and never computed here: the server issues it
     * and derives it again before it accepts the CMS, so the page cannot choose what is signed.
     */
    async function signWithEcp(request: PortalSignatureRequest) {
        setEcpBusyId(request.id);
        setEcpError(null);

        try {
            const signingPackage = await getPortalSigningPackage(request.id);
            const cms = await signPackageHash(signingPackage.packageHash);

            await submitPortalEcpSignature(request.id, cms);
            onSigned();
        } catch (cause: unknown) {
            setEcpError({ requestId: request.id, key: describeSignatureError(cause) });
        } finally {
            setEcpBusyId(null);
        }
    }

    async function sign(request: PortalSignatureRequest) {
        if (strokes === null || !confirmed) {
            return;
        }

        setBusy(true);
        setError(null);

        try {
            await submitPortalSignature(request.id, strokes);
            setOpenId(null);
            onSigned();
        } catch (cause: unknown) {
            // A validation refusal on this route can only be about the strokes, and a validation
            // problem carries no sentence to map — so the status is what says the pad was the issue.
            setError(
                cause instanceof ApiError && cause.status === 400
                    ? 'error.signature_not_drawn'
                    : describeApiError(cause)
            );
        } finally {
            setBusy(false);
        }
    }

    // The same fetch-then-hand-to-a-link download the shared documents use: the route needs the
    // session and a failure has to be said on the page rather than navigated to.
    async function download(request: PortalSignatureRequest, document: PortalSigningDocument) {
        setDownloadingId(document.versionId);
        setUnavailableId(null);

        try {
            const blob = await getPortalSigningDocumentContent(request.id, document.versionId);
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');

            link.href = url;
            link.download = document.fileName;
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            // Almost always a request withdrawn while the page was open.
            setUnavailableId(document.versionId);
        } finally {
            setDownloadingId(null);
        }
    }

    return (
        <ul className="divide-y divide-slate-200/80 dark:divide-slate-800">
            {requests.map((request) => {
                const canDraw = !request.hasSigned && request.requiredMethod !== 'Ecp';
                const canUseEcp = !request.hasSigned && request.requiredMethod !== 'Manual';
                const drawing = openId === request.id;

                return (
                    <li key={request.id} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <p className="font-semibold text-slate-900 dark:text-slate-100">{request.purpose}</p>

                            {request.hasSigned ? (
                                <Badge tone="positive">{t('portal.signature_done')}</Badge>
                            ) : (
                                <Badge tone="warning">{t('portal.signature_awaiting')}</Badge>
                            )}
                        </div>

                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {t('portal.signature_expires')}{' '}
                            {formatPlainDate(request.expiresAt.slice(0, 10), language)}
                        </p>

                        <ul className="mt-3 space-y-2">
                            {request.documents.map((document) => (
                                <li
                                    key={document.versionId}
                                    className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-950"
                                >
                                    <DocumentIcon className="size-5 shrink-0 text-slate-500 dark:text-slate-400" />

                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {document.title}
                                        </p>
                                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                                            {document.fileName}
                                        </p>

                                        {unavailableId === document.versionId && (
                                            <p role="alert" className="mt-1 text-sm text-rose-700 dark:text-rose-400">
                                                {t('portal.document_unavailable')}
                                            </p>
                                        )}
                                    </div>

                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => void download(request, document)}
                                        disabled={downloadingId === document.versionId}
                                    >
                                        <DownloadIcon className="size-4" />
                                        {downloadingId === document.versionId
                                            ? t('portal.document_downloading')
                                            : t('portal.document_download')}
                                    </Button>
                                </li>
                            ))}
                        </ul>

                        {!request.hasSigned && request.requiredMethod === 'Ecp' && (
                            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                                {t('portal.signature_ecp_required')}
                            </p>
                        )}

                        {canDraw && drawing && (
                            <div className="mt-4 space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    {t('portal.signature_read_first')}
                                </p>

                                <SignaturePad
                                    label={t('portal.signature_pad_label')}
                                    placeholder={t('portal.signature_pad_placeholder')}
                                    clearLabel={t('portal.signature_clear')}
                                    disabled={busy}
                                    onChange={setStrokes}
                                />

                                <label className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                                    <input
                                        type="checkbox"
                                        checked={confirmed}
                                        disabled={busy}
                                        onChange={(event) => setConfirmed(event.target.checked)}
                                        className="mt-0.5 size-4 shrink-0 accent-brand-600"
                                    />
                                    <span>{t('portal.signature_consent')}</span>
                                </label>

                                {error !== null && <Alert>{t(error)}</Alert>}

                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => void sign(request)}
                                        disabled={busy || strokes === null || !confirmed}
                                    >
                                        {busy ? t('portal.signature_submitting') : t('portal.signature_submit')}
                                    </Button>

                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setOpenId(null)}
                                        disabled={busy}
                                    >
                                        {t('portal.signature_cancel')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {!drawing && (canDraw || canUseEcp) && (
                            <div className="mt-3 space-y-2">
                                <div className="flex flex-wrap gap-2">
                                    {canDraw && (
                                        <Button size="sm" onClick={() => open(request)} disabled={ecpBusyId !== null}>
                                            <SignatureIcon className="size-4" />
                                            {t('portal.signature_start')}
                                        </Button>
                                    )}

                                    {canUseEcp && (
                                        <Button
                                            size="sm"
                                            variant={canDraw ? 'secondary' : 'primary'}
                                            onClick={() => void signWithEcp(request)}
                                            disabled={ecpBusyId !== null}
                                        >
                                            {ecpBusyId === request.id
                                                ? t('portal.signature_ecp_signing')
                                                : t('portal.signature_ecp_start')}
                                        </Button>
                                    )}
                                </div>

                                {canUseEcp && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        {t('portal.signature_ecp_hint')}
                                    </p>
                                )}

                                {ecpError?.requestId === request.id && (
                                    <Alert>
                                        {t(ecpError.key)}
                                        {ecpError.key === 'error.ncalayer_unavailable' && (
                                            <>
                                                {' '}
                                                <a
                                                    href="https://pki.gov.kz/ncalayer/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="font-semibold underline"
                                                >
                                                    {t('portal.ncalayer_download')}
                                                </a>
                                            </>
                                        )}
                                    </Alert>
                                )}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
